import React from 'react';
import { Box, Card, CardContent, Typography, useTheme } from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { adjustOpacity } from '../../../theme/utils';

const MetricCard = ({
  icon: Icon,
  title,
  value,
  subtitle,
  gradient,
  shadowColor,
}) => {
  const theme = useTheme();
  const contrastText = theme.palette.common.white;

  return (
    <Card
      sx={{
        height: '100%',
        minHeight: 140,
        background: gradient,
        color: contrastText,
        borderRadius: 2,
        boxShadow: `0 4px 12px ${shadowColor}`,
        transition: 'all 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 20px ${shadowColor.replace('0.2', '0.3')}`,
        },
      }}
    >
      <CardContent
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 1.5,
          flex: 1,
        }}
      >
        {/* Icon and Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
          <Icon sx={{ fontSize: '1.25rem', mr: 0.75, color: contrastText }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: contrastText,
              fontSize: '0.9375rem',
            }}
          >
            {title}
          </Typography>
        </Box>
        {/* Value */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: contrastText,
              lineHeight: 1.2,
              fontSize: '1.75rem',
              mb: 0.25,
              textAlign: 'center',
            }}
          >
            {value}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '0.8125rem',
              lineHeight: 1.2,
              textAlign: 'center',
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const SummaryCards = ({ stats }) => {
  const theme = useTheme();

  // Generate gradients from theme colors
  const getGradient = colorName => {
    const color = theme.palette[colorName] || theme.palette.primary;
    const mainColor = color.main;
    const darkColor = color.dark || mainColor;
    return `linear-gradient(135deg, ${mainColor} 0%, ${darkColor} 100%)`;
  };

  const getShadowColor = colorName => {
    const color = theme.palette[colorName] || theme.palette.primary;
    return adjustOpacity(color.main, 0.2);
  };
  const violationsPerTransaction =
    stats?.totalTransactions > 0
      ? (stats?.totalViolations / stats?.totalTransactions).toFixed(2)
      : '0.00';

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        mb: 4,
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        '& > *': {
          flex: '1 1 0',
          minWidth: 0,
        },
      }}
    >
      <MetricCard
        icon={AssessmentIcon}
        title="Total Transactions"
        value={stats?.totalTransactions || 186}
        subtitle="All Time"
        gradient={getGradient('primary')}
        shadowColor={getShadowColor('primary')}
      />
      <MetricCard
        icon={WarningIcon}
        title="Violations Detected"
        value={stats?.totalViolations || 106}
        subtitle="All Time"
        gradient={getGradient('error')}
        shadowColor={getShadowColor('error')}
      />
      <MetricCard
        icon={CheckCircleIcon}
        title="Clean Transactions"
        value={stats?.cleanTransactions || 80}
        subtitle="All Time"
        gradient={getGradient('success')}
        shadowColor={getShadowColor('success')}
      />
      <MetricCard
        icon={TrendingUpIcon}
        title="Violations per Transaction"
        value={violationsPerTransaction}
        subtitle="All Time"
        gradient={getGradient('warning')}
        shadowColor={getShadowColor('warning')}
      />
    </Box>
  );
};

export default SummaryCards;
